import React, { useId, useState } from 'react';
import { AlertCircle, Check, Loader2, Lock, User, X } from 'lucide-react';
import { EMPTY_VALUE_TEXT } from '../constants/profileConfig';
import { getVisibleFields } from '../constants/profileFields';
import { useProfileEdit } from '../hooks/useProfileEdit';
import { FIELD_ICONS } from './profileIcons';
import ProfileSectionCard from './ProfileSectionCard';

/** An input for an editable field, with its icon, character count and error message. */
const EditableField = ({ field, value, error, disabled, onChange, autoFocus }) => {
    const inputId = useId();
    const errorId = `${inputId}-error`;
    const Icon = FIELD_ICONS[field.icon];

    return (
        <div className={`prof-input-group${error ? ' has-error' : ''}`}>
            <label htmlFor={inputId} className="prof-input-label">
                {field.label}
                {field.required && <span className="prof-required" aria-hidden="true"> *</span>}
            </label>
            <div className="prof-input-wrap">
                <span className="prof-input-icon" aria-hidden="true"><Icon size={16} /></span>
                <input
                    id={inputId}
                    type={field.inputType || 'text'}
                    value={value}
                    onChange={(event) => onChange(field.formKey, event.target.value)}
                    maxLength={field.maxLength}
                    placeholder={field.placeholder}
                    autoComplete={field.autoComplete}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    required={field.required}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                />
            </div>
            {error && <p id={errorId} className="prof-input-error" role="alert">{error}</p>}
        </div>
    );
};

/** A field that is shown while editing but cannot be changed here. */
const LockedField = ({ field, profile }) => {
    const Icon = FIELD_ICONS[field.icon];
    const raw = profile[field.key];
    const hasValue = raw !== undefined && raw !== null && raw !== '';

    return (
        <div className="prof-input-group locked">
            <span className="prof-input-label">{field.label}</span>
            <div className="prof-input-wrap" title={field.lockedHint}>
                <span className="prof-input-icon" aria-hidden="true"><Icon size={16} /></span>
                <div className={`prof-locked-value${hasValue && field.personal ? ' dont-translate' : ''}`}>
                    {hasValue ? raw : EMPTY_VALUE_TEXT}
                </div>
                <Lock size={14} className="prof-lock" aria-label={field.lockedHint} />
            </div>
        </div>
    );
};

/**
 * Edit mode of the profile. Editable fields are inputs; the rest stay visible but locked, with a
 * hint about why. A bar at the bottom saves (PATCH /rfp/auth/users/{user_id}) or cancels.
 *
 * @param {{ profile: object, userId: string, onSaved: (updated: object) => void, onCancel: () => void }} props
 */
const ProfileEditForm = ({ profile, userId, onSaved, onCancel }) => {
    const { values, fieldErrors, formError, isDirty, saving, setField, save } = useProfileEdit({
        profile,
        userId,
        onSaved,
    });
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();
        save();
    };

    // Leaving with unsaved changes asks once before throwing them away.
    const handleCancel = () => {
        if (isDirty && !confirmDiscard) {
            setConfirmDiscard(true);
            return;
        }
        onCancel();
    };

    const renderFields = (fields, firstEditableAutoFocus = false) => {
        let autoFocused = !firstEditableAutoFocus;
        return fields.map((field) => {
            if (!field.formKey) return <LockedField key={field.key} field={field} profile={profile} />;
            const shouldFocus = !autoFocused;
            autoFocused = true;
            return (
                <EditableField
                    key={field.key}
                    field={field}
                    value={values[field.formKey]}
                    error={fieldErrors[field.formKey]}
                    disabled={saving}
                    onChange={setField}
                    autoFocus={shouldFocus}
                />
            );
        });
    };

    return (
        <form className="prof-edit-form" onSubmit={handleSubmit} noValidate>
            <div className="prof-cards">
                <ProfileSectionCard title="Profile information" subtitle="Update your name, phone and work details" icon={User} index={0}>
                    <div className="prof-inputs prof-inputs-grid">{renderFields(getVisibleFields(profile), true)}</div>
                </ProfileSectionCard>
            </div>

            <div className="prof-savebar" role="region" aria-label="Save changes">
                <div className="prof-savebar-status" aria-live="polite">
                    {formError ? (
                        <span className="prof-savebar-error"><AlertCircle size={16} aria-hidden="true" />{formError}</span>
                    ) : confirmDiscard ? (
                        <span className="prof-savebar-warn">Discard your changes?</span>
                    ) : isDirty ? (
                        <span className="prof-savebar-dirty"><span className="prof-dot" aria-hidden="true" />You have unsaved changes</span>
                    ) : (
                        <span className="prof-savebar-muted">No changes yet</span>
                    )}
                </div>

                <div className="prof-savebar-actions">
                    {confirmDiscard ? (
                        <>
                            <button type="button" className="prof-btn prof-btn-ghost" onClick={() => setConfirmDiscard(false)}>
                                Keep editing
                            </button>
                            <button type="button" className="prof-btn prof-btn-danger" onClick={onCancel}>
                                <X size={16} aria-hidden="true" />
                                Discard
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="prof-btn prof-btn-ghost" onClick={handleCancel} disabled={saving}>
                                Cancel
                            </button>
                            <button type="submit" className="prof-btn prof-btn-primary" disabled={saving || !isDirty}>
                                {saving
                                    ? <Loader2 size={16} className="prof-spin" aria-hidden="true" />
                                    : <Check size={16} aria-hidden="true" />}
                                {saving ? 'Saving...' : 'Save changes'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </form>
    );
};

export default ProfileEditForm;
